import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./auth";

export interface AIStatus {
  available: boolean;
  provider: string | null;
}

export type FeatureFlags = {
  tier: string;
  ap: boolean;
  ar: boolean;
  financing: boolean;
  automations: boolean;
  contractToCash: boolean;
  aiReports: boolean;
  aiChat: boolean;
  multiEntity: boolean;
  tasks: boolean;
  communications: boolean;
  activityLog: boolean;
  arInstruments: boolean;
  reconciliation: boolean;
  contractInvoicing: boolean;
  apMatching: boolean;
  customPaymentGateways: boolean;
  paymentGateways: boolean;
  externalApi: boolean;
  directDebit: boolean;
  chartOfAccounts: boolean;
  purchaseOrders: boolean;
  deals: boolean;
  maxEntities: string;
  maxUsers: string;
  planChosen: boolean;
  financingGeoAllowed?: boolean;
  directDebitGeoAllowed?: boolean;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionStatus?: string;
  upgradeOnboardingTier?: string | null;
  downgradeNotice?: {
    previousTier: string;
    timestamp: string;
    pausedAutomations: number;
    archivedEntities: number;
  } | null;
};

const DEFAULT_FLAGS: FeatureFlags = {
  tier: "starter",
  ap: true,
  ar: true,
  financing: true,
  automations: true,
  contractToCash: false,
  aiReports: true,
  aiChat: true,
  multiEntity: false,
  tasks: true,
  communications: true,
  activityLog: true,
  arInstruments: false,
  reconciliation: false,
  contractInvoicing: false,
  apMatching: false,
  customPaymentGateways: false,
  paymentGateways: false,
  externalApi: false,
  directDebit: true,
  chartOfAccounts: true,
  purchaseOrders: false,
  deals: false,
  maxEntities: "1",
  maxUsers: "1",
  planChosen: false,
};

const FeaturesContext = createContext<FeatureFlags>(DEFAULT_FLAGS);
const FeaturesLoadingContext = createContext<boolean>(true);
const AIStatusContext = createContext<AIStatus>({ available: true, provider: null });

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const { data, isLoading: featuresLoading } = useQuery<FeatureFlags>({
    queryKey: ["/api/workspace/features"],
    enabled: !!user,
    staleTime: 60_000,
  });

  const { data: aiStatus } = useQuery<AIStatus>({
    queryKey: ["/api/ai/status"],
    staleTime: 300_000,
  });

  const mergedFlags = useMemo(() => {
    const base = data ?? DEFAULT_FLAGS;
    if (aiStatus && !aiStatus.available) {
      return { ...base, aiChat: false };
    }
    return base;
  }, [data, aiStatus]);

  return (
    <AIStatusContext.Provider value={aiStatus ?? { available: true, provider: null }}>
      <FeaturesLoadingContext.Provider value={!!user && featuresLoading}>
        <FeaturesContext.Provider value={mergedFlags}>
          {children}
        </FeaturesContext.Provider>
      </FeaturesLoadingContext.Provider>
    </AIStatusContext.Provider>
  );
}

export function useFeatures(): FeatureFlags {
  return useContext(FeaturesContext);
}

export function useFeaturesLoading(): boolean {
  return useContext(FeaturesLoadingContext);
}

export function useAIStatus(): AIStatus {
  return useContext(AIStatusContext);
}

export function isFinancingAvailable(features: FeatureFlags): boolean {
  return features.financing !== false && features.financingGeoAllowed !== false;
}

export function isDirectDebitAvailable(features: FeatureFlags): boolean {
  return features.directDebit !== false && features.directDebitGeoAllowed !== false;
}
